import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { money } from "@/lib/ui";
import useIsMobile from "@/lib/useIsMobile";
import PrcaConnect from "@/components/PrcaConnect";
import { Loader2, CalendarDays, Trophy, ListChecks, UserCircle, ChevronRight, ArrowLeft, MapPin } from "lucide-react";

const TABS = [
  { id: "schedule", label: "Schedule", icon: CalendarDays },
  { id: "results", label: "Results", icon: ListChecks },
  { id: "mine", label: "My Entries", icon: UserCircle },
];

function fmtRange(a, b) {
  try {
    const opt = { month: "short", day: "numeric" };
    const s = new Date(a).toLocaleDateString("en-US", opt);
    const e = b ? new Date(b).toLocaleDateString("en-US", opt) : "";
    return e && e !== s ? `${s} – ${e}` : s;
  } catch { return ""; }
}

function Schedule({ m, card, title }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/prca/schedule").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (rows === null) return <Spinner />;
  if (!rows.length) return <Empty text="No upcoming PRCA rodeos found." m={m} />;
  return (
    <div className="space-y-2" data-testid="prca-schedule-list">
      {rows.map((r) => (
        <div key={r.RodeoId} className={`rounded-xl border p-4 ${card}`} data-testid={`prca-schedule-${r.RodeoId}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className={`font-display font-bold truncate ${title}`}>{r.Name}</div>
              <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {r.City}, {r.StateAbbrv}{r.VenueName ? ` · ${r.VenueName}` : ""}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-sm font-semibold text-[#003087]">{fmtRange(r.StartDate, r.EndDate)}</div>
              {r.Payout ? <div className="text-xs text-slate-400">{money(r.Payout)} added</div> : null}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Standings({ m, card, title }) {
  const [events, setEvents] = useState([]);
  const [event, setEvent] = useState("AA");
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/prca/events").then((r) => setEvents(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setRows(null);
    api.get(`/prca/standings?event=${event}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [event]);
  const sel = m ? "bg-white/5 border-white/10 text-slate-100" : "bg-white border-[#D7DBE0] text-[#14181F]";
  return (
    <div className="space-y-3">
      <select value={event} onChange={(e) => setEvent(e.target.value)} data-testid="prca-standings-event"
        className={`w-full sm:w-72 rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:border-[#003087] ${sel}`}>
        {events.map((e) => <option key={e.abbrev} value={e.abbrev} className={m ? "bg-[#0C1017]" : ""}>{e.name}</option>)}
      </select>
      {rows === null ? <Spinner /> : !rows.length ? <Empty text="No standings posted for this event." m={m} /> : (
        <div className={`rounded-xl border overflow-hidden ${card}`} data-testid="prca-standings-list">
          {rows.map((r, i) => (
            <div key={i} className={`flex items-center gap-3 px-4 py-3 ${i > 0 ? (m ? "border-t border-white/5" : "border-t border-[#EEF0F2]") : ""}`} data-testid={`prca-standing-${r.place}`}>
              <div className={`w-7 text-center font-display font-bold ${r.place <= 3 ? "text-[#003087]" : "text-slate-400"}`}>{r.place}</div>
              {r.photo ? <img src={r.photo} alt="" className="w-9 h-9 rounded-full object-cover bg-slate-200" onError={(e) => { e.target.style.display = "none"; }} /> : null}
              <div className="min-w-0 flex-1">
                <div className={`font-semibold truncate ${title}`}>{r.name}</div>
                {r.hometown ? <div className="text-xs text-slate-400 truncate">{r.hometown}</div> : null}
              </div>
              <div className="text-right shrink-0"><div className="text-sm font-bold text-[#3B6B1E]">{money(r.earnings || 0)}</div></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Results({ m, card, title }) {
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  useEffect(() => { api.get("/prca/results").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);

  const open = async (r) => {
    setSel(r); setDetail(null);
    try { const { data } = await api.get(`/prca/results/${r.rodeo_id}`); setDetail(data); }
    catch { setDetail({ error: true }); }
  };

  if (sel) {
    const html = (detail?.ap_results || "").replace(/<script[\s\S]*?<\/script>/gi, "");
    return (
      <div>
        <button onClick={() => { setSel(null); setDetail(null); }} data-testid="prca-results-back" className="flex items-center gap-1.5 text-sm font-semibold text-[#003087] mb-3"><ArrowLeft className="w-4 h-4" /> All results</button>
        <div className={`rounded-xl border p-5 ${card}`} data-testid="prca-results-detail">
          <div className={`font-display font-bold text-lg ${title}`}>{sel.name}</div>
          <div className="text-xs text-slate-400 mb-4">{sel.city}, {sel.state}</div>
          {detail === null ? <Spinner /> : detail.error ? <Empty text="No results posted yet." m={m} /> : (
            <div className={`prca-ap text-sm leading-relaxed ${m ? "text-slate-300" : "text-[#3A424D]"}`}
              dangerouslySetInnerHTML={{ __html: html }} />
          )}
        </div>
      </div>
    );
  }
  if (rows === null) return <Spinner />;
  if (!rows.length) return <Empty text="No recent PRCA results." m={m} />;
  return (
    <div className="space-y-2" data-testid="prca-results-list">
      {rows.map((r) => (
        <button key={r.rodeo_id} onClick={() => open(r)} data-testid={`prca-result-${r.rodeo_id}`}
          className={`w-full text-left rounded-xl border p-4 flex items-center justify-between gap-3 transition-colors ${card} ${m ? "hover:bg-white/10" : "hover:bg-[#FAFBFC]"}`}>
          <div className="min-w-0">
            <div className={`font-display font-bold truncate ${title}`}>{r.name}</div>
            <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {r.city}, {r.state} · {fmtRange(r.start_date, r.end_date)}</div>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
        </button>
      ))}
    </div>
  );
}

const Spinner = () => <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#003087]" /></div>;
const Empty = ({ text, m }) => <div className={`rounded-xl border border-dashed p-8 text-center text-sm ${m ? "border-white/15 text-slate-400" : "border-[#D7DBE0] text-slate-500"}`}>{text}</div>;

export default function PRCA() {
  const m = useIsMobile();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "schedule";
  const setTab = (t) => setSp({ tab: t });
  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "";
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const title = m ? "text-slate-100" : "text-[#14181F]";

  return (
    <div className={page} data-testid="prca-page">
      <div className={`px-4 sm:px-6 py-5 border-b flex items-center gap-2 ${m ? "border-white/10" : "border-[#E4E7EB]"}`}>
        <button onClick={() => nav("/directory")} data-testid="prca-back-directory" className="mr-1 text-slate-400 hover:text-[#003087]" title="Back to Directory"><ArrowLeft className="w-5 h-5" /></button>
        <Trophy className="w-6 h-6 text-[#003087]" />
        <div>
          <h1 className={`font-display font-bold text-xl ${title}`}>PRCA ProRodeo</h1>
          <p className="text-xs text-slate-400">Schedule · Results</p>
        </div>
      </div>

      <div className={`px-4 sm:px-6 pt-4 border-b ${m ? "border-white/10" : "border-[#E4E7EB]"}`}>
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon; const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`prca-tab-${t.id}`}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${active ? "border-[#003087] text-[#003087]" : `border-transparent ${m ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-[#14181F]"}`}`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-4 sm:px-6 py-5 max-w-3xl">
        {tab === "schedule" && <Schedule m={m} card={card} title={title} />}
        {tab === "standings" && <Standings m={m} card={card} title={title} />}
        {tab === "results" && <Results m={m} card={card} title={title} />}
        {tab === "mine" && <PrcaConnect m={m} />}
      </div>
    </div>
  );
}
